import React from 'react';
import { useDispatch } from 'react-redux';
import { FormattedDate } from 'react-intl';
import {
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow
} from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid-premium';

import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';
import { ButtonLink, Icon, useSettings, Utils } from 'app/main/components';
import { ViewTable, ViewTableUtils } from 'app/main/components/page-layouts';

function DemsUrlsClickDetailsDialog({ clicks }: { clicks: any[] }) {
	const dispatch = useDispatch();
	const { t } = useSettings();

	return (
		<>
			<DialogTitle>{t('DIALOG_TITLE_RECIPIENTS_CLICK_DETAILS')}</DialogTitle>
			<DialogContent>
				<TableContainer component={Paper} variant="outlined">
					<Table className="tabular-nums" size="small">
						<TableBody>
							{clicks.map(click => (
								<TableRow
									key={click.dclk_uid}
									sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
								>
									{!!click.dclk_ip && <TableCell>{click.dclk_ip}</TableCell>}
									{!!click.dclk_date && (
										<TableCell align="right">
											<FormattedDate
												value={Utils.parseDate(click.dclk_date)?.toDate()}
												year="numeric"
												month="numeric"
												day="numeric"
												hour="numeric"
												minute="numeric"
												second="numeric"
											/>
										</TableCell>
									)}
									<TableCell>
										<ButtonLink href={`mailto:${click.mail_title}`}>{click.mail_title}</ButtonLink>
									</TableCell>
									<TableCell align="right">x{click.dclk_count}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => dispatch(closeDialog())}>{t('DIALOG_ACTION_CLOSE')}</Button>
			</DialogActions>
		</>
	);
}

export default function DemsClicksTab({ demUid }: { demUid: string }) {
	const dispatch = useDispatch();
	const { t } = useSettings();

	const [refresh, setRefresh] = React.useState(false);
	const updateRefresh = () => setRefresh(state => !state);

	const columns: GridColDef[] = [
		{
			field: 'durl_url',
			headerName: t('DEMS_URLS_COLUMN_LINK'),
			minWidth: 200,
			flex: 1,
			renderCell: ({ value }) => (
				<ButtonLink href={value} icon="link">
					{value}
				</ButtonLink>
			),
			sortable: false,
			hideable: false
		},
		{
			field: 'durl_clicks',
			type: 'number',
			headerName: t('DEMS_URLS_COLUMN_CLICKS'),
			renderCell: ({ value, row }) =>
				ViewTableUtils.renderSquareChip({
					value: `${value}`,
					backgroundColor: theme => theme.palette.success.main,
					icon: <Icon icon="cursor-default-click-outline" size={18} />,
					onClick: () =>
						dispatch(
							openDialog({
								children: <DemsUrlsClickDetailsDialog clicks={row.durl_clicks_list} />,
								maxWidth: 'md',
								fullWidth: true
							})
						),
					disabled: value === 0
				}),
			sortable: false
		}
	];

	return (
		<ViewTable
			module="dems_urls"
			columns={columns}
			defaultFilters={[
				{
					filt_field: 'durl_dems_uid',
					filt_clause: 'equals',
					filt_value: demUid
				}
			]}
			refresh={refresh}
			updateRefresh={updateRefresh}
			showFilters={false}
			showExportButton={false}
		/>
	);
}
